import { yupResolver } from '@hookform/resolvers/yup';
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  TextField,
  Select,
  MenuItem,
  Typography,
  Grid,
  InputLabel,
  DialogTitle,
  IconButton,
  withStyles,
} from '@material-ui/core';
import { CloseOutlined } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { getPlatformList } from 'api/cms/platform';
import { getPlatformEnableProviderList, getProviderList } from 'api/cms/provider';
import { createOrUpdateUserGroup, createUserGroup } from 'api/cms/user_group';
import { PropTypes } from 'prop-types';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import useSWR from 'swr';
import * as yup from 'yup';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

const CustomDialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <DialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseOutlined />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
});

const schema = yup.object().shape({
  group_code: yup.string().required('Group code is required'),
  provider_code: yup.string().required('Provider is required'),
  platform_codes: yup.array().min(1, 'Platform must be at least 1'),
  configs: yup.string().test('is-json', 'Invalid JSON format', (value) => {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }),
});

const UserGroupDialog = (props) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      group_code: '',
      provider_code: '',
      platform_codes: [],
      configs: '',
    },
  });
  const { data: providers } = useSWR('/provider/list', () => getProviderList());
  const { data: platforms } = useSWR('/platform/list', () => getPlatformList());

  useEffect(() => {
    if (props.row) {
      setValue('group_code', props.row.key);
      setValue('provider_code', props.row.provider_code);

      setValue(
        'platform_codes',
        props.row.platforms.map((p) => p),
      );
      setValue('configs', JSON.stringify(props.row.provider_configs) || '');
    }
  }, [props]);

  const handleClickClose = () => {
    reset();
    props.handleClose();
  };

  const renderAutoComplete = (option) => {
    let merchantLabel = '';
    if (option.merchant_code !== '') {
      merchantLabel += `(${option.merchant_code})`;
    }
    return `${option.platform_name} (${option.platform_code}) ${merchantLabel}`;
  };

  const onSubmit = async (values) => {
    // console.log(values);

    if (props.row) {
      console.log(values);
      const configs = JSON.parse(values.configs);
      const platform_codes = values.platform_codes.map((p) => p.platform_code);
      const res = await createOrUpdateUserGroup({
        group_code: values.group_code,
        provider_code: values.provider_code,
        platform_codes: platform_codes,
        configs: configs,
      });
      if (res.code === 0) {
        handleClickClose();
        reset();
        toast.success('updated');
      } else {
        toast.error(`${res.data.code} ${res.data.message}`);
      }
    } else {
      const configs = JSON.parse(values.configs);
      const platform_codes = values.platform_codes.map((p) => p.platform_code);
      const res = await createUserGroup({
        group_code: values.group_code,
        provider_code: values.provider_code,
        platform_codes: platform_codes,
        configs: configs,
      });
      if (res.code === 0) {
        handleClickClose();
        reset();
        toast.success('created');
      } else {
        toast.error(`${res.data.code} ${res.data.message}`);
      }
    }
  };

  return (
    <Box>
      <Dialog
        id="user-group-dialog"
        open={props.open}
        onClose={handleClickClose}
        aria-labelledby="popup-user-group-dialog-title"
        aria-describedby="popup-user-group-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <CustomDialogTitle onClose={handleClickClose}>Create New Group</CustomDialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" flexDirection="column" gridGap={3}>
                  <Controller
                    name="group_code"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Group Code"
                        error={!!errors?.group_code}
                        helperText={errors?.group_code?.message}
                      />
                    )}
                  />

                  <Controller
                    name="provider_code"
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <TextField
                          id="select-provider"
                          select
                          label="Select Provider"
                          value={field.value}
                          onChange={(e) => {
                            const value = e.target.value;
                            setValue('provider_code', value);
                          }}
                          error={!!errors?.provider_code}
                          helperText={errors?.provider_code?.message}
                        >
                          <MenuItem value="" disabled>
                            Select Provider
                          </MenuItem>
                          {providers.map((p) => (
                            <MenuItem key={p.provider_code} value={p.provider_code}>
                              {p.provider_name} ({p.provider_code})
                            </MenuItem>
                          ))}
                        </TextField>
                      );
                    }}
                  />

                  <Controller
                    name="platform_codes"
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <>
                          <InputLabel>Platform</InputLabel>
                          <Autocomplete
                            {...field}
                            label="Platform"
                            multiple
                            fullWidth
                            filterSelectedOptions
                            options={platforms}
                            getOptionLabel={(option) => renderAutoComplete(option)}
                            onChange={(e, value) => {
                              console.log(value);
                              // const value = e.target.value;
                              setValue('platform_codes', value);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={!!errors?.platform_codes}
                                helperText={errors?.platform_codes?.message}
                              />
                            )}
                          />
                          {/* <Select
                            {...field}
                            label="Platform"
                            multiple
                            fullWidth
                            value={field.value}
                            onChange={(e) => {
                              const value = e.target.value;
                              setValue('platform_codes', value);
                            }}
                            error={!!errors?.platform_codes}
                            helperText={errors?.platform_codes?.message || 'test'}
                          >
                            <MenuItem value="" disabled>
                              Select Platform
                            </MenuItem>
                            {platforms.map((p) => (
                              <MenuItem key={p.platform_code} value={p.platform_code}>
                                {p.platform_name} ({p.platform_code})
                              </MenuItem>
                            ))}
                          </Select> */}
                        </>
                      );
                    }}
                  />

                  <Controller
                    name="configs"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Config"
                        multiline
                        maxRows={10}
                        minRows={5}
                        fullWidth
                        error={!!errors?.configs}
                        helperText={errors?.configs?.message}
                      />
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
            <DialogContentText id="popup-dialog-description"></DialogContentText>
            <DialogActions>
              <Button color="primary" variant="contained" type="submit">
                {props.row ? 'Update Group' : 'Create Group'}
              </Button>
            </DialogActions>
          </DialogContent>
        </Box>
      </Dialog>
    </Box>
  );
};

UserGroupDialog.propTypes = {
  row: PropTypes.object,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default UserGroupDialog;
